/**
 * Service layer — dependency-injected implementations for the sync engine.
 *
 * The singleton factory `getSyncService()` lazily creates a SyncService
 * wired with the web platform adapters (WebSyncStorage, WebSyncHttpClient,
 * WebSyncEventBus).
 */

export { ImageUploadService } from './imageUploadService'
export type {
  ImageQueueStorage,
  ImageQueueItem,
  UploadResult,
} from './imageUploadService'
export { getImageUploadService } from './imageUploadServiceInstance'
export { SyncService } from "./syncService";
export type { SyncServiceDeps } from "./syncService";
export { WebSyncStorage } from "./syncStorage";
export { WebSyncHttpClient } from "./syncHttpClient";
export { WebSyncEventBus } from "./syncEventBus";

import { SyncService } from "./syncService";
import { WebSyncStorage } from "./syncStorage";
import { WebSyncHttpClient } from "./syncHttpClient";
import { WebSyncEventBus } from "./syncEventBus";
import { useAuthStore } from "../../store/auth";
import { extractTitleFromContent } from "../../hooks/useEditorAutosave";
import { flushOpenEditor } from "../openEditorRegistry";
import { WebSyncDevice } from "../syncDevice";

let _syncService: SyncService | null = null;
let _storage: WebSyncStorage | null = null;
let _device: WebSyncDevice | null = null;

/** The storage the sync service writes through (the live socket shares it). */
export function getContentStore(): WebSyncStorage {
  if (!_storage) _storage = new WebSyncStorage({
      deriveTitle: extractTitleFromContent,
      getUserId: () => useAuthStore.getState().user?.id ?? null,
    });
  return _storage;
}

/** This tab's content sync identity (shared by HTTP and the live socket). */
export function getSyncDevice(): WebSyncDevice {
  if (!_device) _device = new WebSyncDevice();
  return _device;
}

/** Get (or create) the singleton SyncService wired with web platform adapters. */
export function getSyncService(): SyncService {
  if (!_syncService) {
    const storage = getContentStore();
    _syncService = new SyncService({
      storage,
      http: new WebSyncHttpClient(),
      events: new WebSyncEventBus(),
      getUserId: () => useAuthStore.getState().user?.id ?? "",
      content: { store: storage, device: getSyncDevice(), beforeRemote: flushOpenEditor },
    });
  }
  return _syncService;
}

/** Get the WebSyncEventBus singleton (for SyncIndicator subscriptions). */
let _eventBus: WebSyncEventBus | null = null;
export function getSyncEventBus(): WebSyncEventBus {
  if (!_eventBus) {
    _eventBus = new WebSyncEventBus();
  }
  return _eventBus;
}
